<template>
  <div class="dashboard-container">
    <link href="https://api.mapbox.com/mapbox-gl-js/v3.1.2/mapbox-gl.css" rel="stylesheet">
    <!-- En-tête du dashboard -->
    <div class="dashboard-header">
      <div class="header-content">
        <h1 class="dashboard-title">
          <i class="el-icon-data-analysis" />
          Dashboard SCIAGRI
        </h1>
        <div class="header-actions">
          <el-button
            type="primary"
            icon="el-icon-refresh"
            size="medium"
            :loading="loading"
            @click="refreshDashboard"
          >
            Actualiser
          </el-button>
          <el-select
            v-model="camp_id"
            placeholder="Campagne Agricole"
            @change="setCampId(camp_id)"
          >
            <el-option
              v-for="item in campagnes_agricoles"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
          <span class="last-update">
            Dernière mise à jour: {{ lastUpdate }}
          </span>
        </div>
      </div>
    </div>

    <!-- Zone de contenu principal -->
    <div class="dashboard-content">
      <!-- Message de chargement -->
      <!-- IMPORTANT: v-show pour ne pas détruire/remonter les composants (KpiPanel) -->
      <div v-show="loading" class="loading-container">
        <i class="el-icon-loading" />
        <p>{{ loadingMessage }}</p>
      </div>

      <!-- Contenu principal -->
      <div v-show="!loading" class="main-content">
        <!-- Filtre KPIs Panel -->
        <el-card class="kpis-card" shadow="hover">
          <div slot="header" class="card-header">
            <span class="card-title">📊 Filtre</span>
          </div>
          <KpiFilterPanel
            ref="kpiFilterPanel"
            :kpis="currentKpis"
          />
        </el-card>

        <!-- Graphiques -->
        <el-card class="charts-card" shadow="hover" v-show="false">
          <div slot="header" class="card-header">
            <span class="card-title">📊 Graphiques</span>
          </div>
          <ChartsPanel
            :parcelles="parcelles"
            :controleurs="controleurs"
            :producteurs="producteurs"
            :groupements="groupements"
            :unites-productions="unitesProductions"
            :audits="audits"
            :controles-internes="controlesInternes"
            :formations="formations"
            :cultures="cultures"
            :parcelles-produits="parcellesProduits"
            :parcelles-producteurs="parcellesProducteurs"
            :data-quantite-collectee="dataQuantiteCollectee"
          />
        </el-card>

        <el-row :gutter="20" class="charts-row" v-show="false">
          <el-col :xs="24" :sm="24" :lg="12">
            <CertificationTable :data="certificationStats" :loading="loading" />
          </el-col>
          <el-col :xs="24" :sm="24" :lg="12">
            <AntenneTable />
          </el-col>
        </el-row>

        <!-- Carte GPS des parcelles -->
        <el-card class="map-card" shadow="hover">
          <div slot="header" class="card-header">
            <span class="card-title">🗺️ Carte GPS</span>
          </div>
          <ParcellesMap :show-filters="true" />
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Resource from '@/api/resource';
import KpiFilterPanel from './components/KpiFilterPanel.vue';
import CertificationTable from './components/CertificationTable.vue';
import AntenneTable from './components/AntenneTable.vue';
import ParcellesMap from './components/ParcellesMap.vue';
import ChartsPanel from './components/ChartsPanel.vue';
import DashboardAPI from '@/api/dashboard';

const campagnesResource = new Resource('campagne_agricoles');

export default {
  name: 'DashboardAdmin',
  components: {
    KpiFilterPanel,
    CertificationTable,
    AntenneTable,
    ParcellesMap,
    ChartsPanel,
  },
  filters: {
    numberFormat(value) {
      if (!value) {
        return '0';
      }
      return new Intl.NumberFormat('fr-FR').format(value);
    },
  },
  data() {
    return {
      loading: false,
      lastUpdate: null,
      certificationStats: [],
      campagnes_agricoles: [],
      camp_id: null,
      camp_label: null,
      loadingMessage: 'Chargement des données...',
      currentKpis: {},
    };
  },
  computed: {
    ...mapGetters('dashboard', ['filters', 'activeFilters']),
  },
  async created() {
    this.loading = true;
    this.loadingMessage;
    await this.campEnCours();
    this.loadFilterOptions();
    await this.getCampagnesList();
    this.fetchData();
    this.loading = false;
  },

  async mounted() {
    // Écouter l'événement pour activer/désactiver le loading lors de l'application des filtres
    this.$root.$on('kpi-filters-loading', (data) => {
      if (data && typeof data.loading !== 'undefined') {
        this.loading = data.loading;
        if (data.message) {
          this.loadingMessage = data.message;
        }
      }
    });

    // Écouter les mises à jour des KPIs depuis KpiFilterPanel
    this.$root.$on('kpi-data-updated', (kpis) => {
      this.currentKpis = kpis || {};
    });
  },
  beforeDestroy() {
    // Nettoyer les écouteurs d'événements
    this.$root.$off('kpi-filters-loading');
    this.$root.$off('kpi-data-updated');
  },
  methods: {
    ...mapActions('dashboard', ['loadFilterOptions']),
    handleFiltersRefresh(filters) {
      this.fetchData();
    },

    async refreshDashboard() {
      this.loading = true;
      // Réinitialiser tous les filtres dans le store
      await this.$store.dispatch('dashboard/resetFilters');

      // Recharger les données
      await this.loadInitialLists();
      this.fetchData();
    },

    async fetchData() {
      this.loading = true;

      try {
        const filters = { ...this.$store.getters['dashboard/filters'] };

        // Ajouter la campagne_id aux filtres si elle est définie
        if (this.camp_id) {
          filters.campagne_id = this.camp_id;
        }

        // Appel API Certification
        try {
          const responseCertif = await DashboardAPI.fetchCertificationStats(
            filters
          );
          this.formatCertificationData(responseCertif.data);
        } catch (e) {
          console.warn(
            'API Certification non disponible, utilisation de données mockées'
          );
          // Mock data pour la démo (Structure fournie par l'utilisateur)
          const mockData = {
            surface_bio_totale: 150.5,
            surface_totale: 300.5,
            pourcentage_bio: 50.08,
            surface_bio_par_culture: [
              {
                nom: 'Cacao',
                surface_bio: 100,
                surface_totale: 200,
                pourcentage_bio: 50,
              },
              {
                nom: 'Café',
                surface_bio: 50.5,
                surface_totale: 100.5,
                pourcentage_bio: 50.25,
              },
            ],
            objectifs: {
              surface_bio_objectif: 0,
              pourcentage_bio_objectif: 50,
            },
          };
          this.formatCertificationData(mockData);
        }

        this.updateLastUpdate();
        // this.$message.success('Données actualisées avec succès');
      } catch (error) {
        console.error('Erreur chargement dashboard:', error);
        this.$message.error('Erreur lors du chargement des données');
      } finally {
        this.loading = false;
      }
    },

    formatCertificationData(data) {
      if (!data) {
        return;
      }
      const rows = [];

      // Ligne Globale
      rows.push({
        indicateur: 'Surface Biologique Totale',
        surface: data.surface_bio_totale,
        surface_totale: data.surface_totale,
        pourcentage: data.pourcentage_bio,
        objectif: data.objectifs ? data.objectifs.pourcentage_bio_objectif : 0,
        is_total: true, // Flag pour style éventuel
      });

      // Lignes par culture
      if (data.surface_bio_par_culture) {
        data.surface_bio_par_culture.forEach((c) => {
          rows.push({
            indicateur: c.nom,
            surface: c.surface_bio,
            surface_totale: c.surface_totale,
            pourcentage: c.pourcentage_bio,
            // Pas d'objectif par culture dans le JSON, on met null
            objectif: null,
          });
        });
      }

      this.certificationStats = rows;
    },

    updateLastUpdate() {
      const now = new Date();
      this.lastUpdate = now.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },

    async getCampagnesList() {
      const { data } = await campagnesResource.list();
      this.campagnes_agricoles = [];
      data.forEach((camp) => {
        this.campagnes_agricoles.push({ value: camp.id, label: camp.periode });
      });
    },
    async campEnCours() {
      const { data } = await campagnesResource.sublist('/en_cours');
      this.camp_id = data.id;
      this.camp_label = data.periode;
      sessionStorage.setItem('camp_id', this.camp_id);
      sessionStorage.setItem('camp_label', this.camp_label);
      await this.$store.dispatch('camp_agricole/set_camp', this.camp_id);
      await this.$store.dispatch('camp_agricole/set_camp_label', this.camp_label);
      console.log('camp_encours', data);
    },

    async setCampId(id) {
      // Mettre à jour la campagne dans le store
      await this.$store.dispatch('camp_agricole/set_camp', id);
      // Trouver le label correspondant si déjà chargé
      const found = this.campagnes_agricoles.find(c => c.value === id);
      if (found && found.label) {
        await this.$store.dispatch('camp_agricole/set_camp_label', found.label);
      }
      sessionStorage.setItem('camp_id', id);
      sessionStorage.setItem('camp_label', found.label);
      this.camp_id = id;
      this.camp_label = found.label;
      await this.loadInitialLists();
      this.fetchData();
    },
    // Méthode utilitaire pour afficher les unités de production de ADJOLO
    // Peut être appelée depuis la console : this.$refs.dashboard.$children[0].showAdjoloUnites()
  },
};
</script>

<style lang="scss" scoped>
.dashboard-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 20px;
}

.dashboard-header {
  background: linear-gradient(135deg, #00ab53 0%, #00d977 100%);
  border-radius: 12px;
  padding: 24px 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 171, 83, 0.2);

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
  }

  .dashboard-title {
    color: white;
    font-size: 28px;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 12px;

    i {
      font-size: 32px;
    }
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 20px;

    .el-button {
      background: rgba(255, 255, 255, 0.2);
      border-color: rgba(255, 255, 255, 0.5);
      color: white;
      font-weight: 500;
      padding: 10px 20px;
      border-radius: 6px;
      transition: all 0.3s ease;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      &:hover {
        background: rgba(255, 255, 255, 0.3);
        border-color: white;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      &:active {
        transform: translateY(0);
      }

      &.is-loading {
        background: rgba(255, 255, 255, 0.15);
      }
    }

    .el-select {
      min-width: 200px;
    }

    .last-update {
      color: rgba(255, 255, 255, 0.9);
      font-size: 14px;
    }
  }
}

.dashboard-content {
  .loading-container {
    text-align: center;
    padding: 60px 20px;
    color: #606266;

    i {
      font-size: 48px;
      color: #00ab53;
      animation: rotate 1s linear infinite;
    }

    p {
      margin-top: 16px;
      font-size: 16px;
    }
  }

  .main-content {
    .kpis-card,
    .chart-card {
      border-radius: 8px;
      margin-bottom: 20px;
      border: 1px solid #e4e7ed;
      transition: all 0.3s ease;

      &:hover {
        box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0;

        .card-title {
          font-size: 18px;
          font-weight: 600;
          color: #303133;
        }
      }
    }

    .kpis-placeholder,
    .chart-placeholder {
      padding: 40px 20px;
      text-align: center;
      color: #909399;
      min-height: 200px;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;

      p {
        margin: 8px 0;
        font-size: 16px;
      }

      .filters-info {
        margin-top: 20px;
        padding: 12px;
        background: #f5f7fa;
        border-radius: 6px;
        font-size: 14px;
        color: #606266;

        span {
          color: #00ab53;
          font-weight: 500;
        }
      }
    }

    .table-container {
      padding: 10px;
    }
  }
}

.charts-row {
  margin-top: 20px;
  margin-bottom: 20px;
}

.charts-card {
  margin-bottom: 20px;
}

@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

// Responsive
@media (max-width: 768px) {
  .dashboard-header {
    padding: 16px 20px;

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .dashboard-title {
      font-size: 22px;
    }

    .header-actions {
      width: 100%;
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .dashboard-container {
    padding: 12px;
  }
}
</style>
